import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, Briefcase, ArrowRight, ChevronDown, ChevronUp, User } from 'lucide-react';

const CategorySection = ({ title, members, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mb-4" data-testid={`category-${title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        data-testid={`category-toggle-${title.toLowerCase().replace(/[^a-z]/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <span className="text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            {members.length}
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="p-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member, index) => (
              <div
                key={member.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300"
                data-testid={`staff-card-${member.id}`}
              >
                {/* Image */}
                <div className="aspect-square overflow-hidden bg-gray-100">
                  {member.image_url ? (
                    <img
                      src={member.image_url}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
                      <User className="w-20 h-20 text-blue-300" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <h4 className="text-base font-bold text-gray-900 mb-1">{member.name}</h4>
                  <p className="text-blue-600 font-medium text-sm mb-3">{member.title}</p>

                  {/* Credentials */}
                  {member.credentials && member.credentials.length > 0 && (
                    <div className="flex items-start gap-2 mb-3">
                      <Award className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex flex-wrap gap-1">
                        {member.credentials.map((cred, idx) => (
                          <span key={cred} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                            {cred}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Experience */}
                  <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
                    <Briefcase className="w-4 h-4" />
                    <span>{member.years_experience}+ years experience</span>
                  </div>

                  {/* Bio */}
                  <p className="text-sm text-gray-600 mb-3 line-clamp-3">{member.bio}</p>

                  {/* View Profile Link */}
                  <Link
                    to={`/team/${member.id}`}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                    data-testid={`view-profile-${member.id}`}
                  >
                    View Full Profile
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StaffSection = ({ staff }) => {
  const categories = [
    { title: 'Executive Leadership', key: 'Executive Leadership' },
    { title: 'Licensed Providers & Specialists', key: 'Licensed Providers & Specialists' },
    { title: 'Case Work Specialists', key: 'Case Work Specialists' }
  ];

  const groupedStaff = categories.map(cat => ({
    ...cat,
    members: staff.filter(s => s.category === cat.key)
  }));

  return (
    <section id="team" className="py-20 bg-white" data-testid="staff-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Staff Categories */}
        {groupedStaff.map((group, index) => (
          <CategorySection
            key={group.key}
            title={group.title}
            members={group.members}
            defaultOpen={index === 0}
          />
        ))}

        {/* Trust Badge */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col items-center gap-6">
            <div className="flex items-center gap-4 bg-blue-50 px-8 py-4 rounded-lg">
              <Award className="w-8 h-8 text-blue-600" />
              <div className="text-left">
                <p className="font-bold text-gray-900">Licensed & Accredited</p>
                <p className="text-sm text-gray-600">All providers are fully licensed in Maryland</p>
              </div>
            </div>

            {/* Regulatory Logos */}
            <div className="flex gap-6 items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/c0a5v1h1_mrd-768x189.png"
                alt="Maryland Department of Health"
                className="h-14 w-auto"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/jwyrvdww_carf_logo-500x500.png"
                alt="CARF Accredited"
                className="h-14 w-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StaffSection;
